import type { FC } from 'react';
import { useSearchParams } from 'react-router';
import { getDocumentationUrl } from '@gonfalon/docs/documentationUrl';
import { areAgenticOnboardingPromptUpdatesEnabled } from '@gonfalon/dogfood-flags/dogfood-flags';
import { NewTabLink } from '@gonfalon/launchpad-experimental/NewTabLink';
import type { SdkKey } from '@gonfalon/sdk-info/sdkInfo';
import { Heading, Tab, TabList, TabPanel, Tabs, Text } from '@launchpad-ui/components';
import cx from 'clsx';

import { trackSdkSetupResourceLinkClicked, trackSdkSetupTabSelected } from '../../analytics';
import { AgentCommand } from '../AgentCommand/AgentCommand';
import { ChooseSDK } from '../ChooseSDK/ChooseSDK';
import { SdkSetupContent } from '../SdkSetupContent/SdkSetupContent';
import { SdkSetupVerifyConnectionSection } from '../SdkSetupVerifyConnectionSection/SdkSetupVerifyConnectionSection';

import styles from './SdkSetupTabs.module.css';

interface SdkSetupTabsProps {
  sdk: SdkKey | undefined;
  onSdkSelect: (sdk: SdkKey) => void;
  variant?: 'drawer' | 'page';
  className?: string;
}

export const SdkSetupTabs: FC<SdkSetupTabsProps> = ({ sdk, onSdkSelect, variant = 'drawer', className }) => {
  const Aside = variant === 'page' ? 'aside' : 'div';
  const Main = variant === 'page' ? 'main' : 'div';
  const enablePromptUpdates = areAgenticOnboardingPromptUpdatesEnabled();
  const [searchParams] = useSearchParams();
  const defaultTab = searchParams.get('preferManual') === 'true' ? 'manual' : 'ai';

  return (
    <Tabs
      defaultSelectedKey={defaultTab}
      className={cx(styles.tabs, variant === 'page' && styles.tabsPage, className)}
      onSelectionChange={(key) => trackSdkSetupTabSelected(String(key))}
    >
      <TabList className={styles.tabList}>
        <Tab id="ai" className={styles.tab}>
          {enablePromptUpdates ? 'Install with AI' : 'Connect with AI'}
        </Tab>
        <Tab id="manual" className={styles.tab}>
          {enablePromptUpdates ? 'Install manually' : 'Connect manually'}
        </Tab>
      </TabList>
      <TabPanel id="ai" className={styles.aiPanel}>
        <div className={styles.aiContent}>
          <AgentCommand />
          {enablePromptUpdates ? (
            <div className={styles.whatHappensNextContainer}>
              <section className={styles.whatHappensNext}>
                <Heading level={3} className={styles.whatHappensNextHeading}>
                  What happens next
                </Heading>
                <ul className={styles.whatHappensNextList}>
                  <li>Detects your stack — language, framework, package manager.</li>
                  <li>Installs the right LaunchDarkly SDK and wires it into your code.</li>
                  <li>Creates your first feature flag and verifies it's working.</li>
                </ul>
                <Text className={styles.helperText}>Usually 3–5 minutes.</Text>
              </section>
              <Text className={styles.resourcesText}>
                More on{' '}
                <NewTabLink
                  href={getDocumentationUrl('home/getting-started/mcp')}
                  text="setting up the MCP server"
                  showIcon={false}
                  onClick={() => trackSdkSetupResourceLinkClicked('mcp-server')}
                />{' '}
                and{' '}
                <NewTabLink
                  href={getDocumentationUrl('home/getting-started/skills')}
                  text="available agent skills"
                  showIcon={false}
                  onClick={() => trackSdkSetupResourceLinkClicked('agent-skills')}
                />
                .
              </Text>
            </div>
          ) : (
            <Text className={styles.resourcesText}>
              Learn more about{' '}
              <NewTabLink
                href={getDocumentationUrl('home/getting-started/mcp')}
                text="setting up the MCP server"
                showIcon={false}
                onClick={() => trackSdkSetupResourceLinkClicked('mcp-server')}
              />{' '}
              or{' '}
              <NewTabLink
                href={getDocumentationUrl('home/getting-started/skills')}
                text="available agent skills"
                showIcon={false}
                onClick={() => trackSdkSetupResourceLinkClicked('agent-skills')}
              />
              .
            </Text>
          )}
          <SdkSetupVerifyConnectionSection key={sdk ?? 'no-sdk'} />
        </div>
      </TabPanel>
      <TabPanel id="manual" className={styles.manualPanel}>
        <div className={styles.scrollbarContainer}>
          <div className={styles.layout}>
            <Aside className={styles.aside}>
              <ChooseSDK onSelect={onSdkSelect} selectedSdk={sdk} />
            </Aside>
            <Main className={styles.main}>
              <SdkSetupContent />
            </Main>
          </div>
        </div>
      </TabPanel>
    </Tabs>
  );
};
